import { validateBudgetRange } from '@/components/ModifyBudget';
import { ModifyBudgetRow } from '@/components/ModifyBudgetRow';
import { SubmitFunc } from '@/dashboards/BudgetOptimization/types';
import { useBudgetOptimizationContext } from '@/dashboards/BudgetOptimization/hooks/useBudgetOptimizationContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { FEATURE_FLAG_SUBJECTS } from '@/features/Casl';
import React from 'react';

interface Props {
    variable: string;
    modalResetKey?: number;
    onOutOfBoundsChannel?: (info: {
        channelName: string;
        min: number;
        max: number;
    }) => void;
    onSubmit: SubmitFunc;
    originalLimitsRef: React.MutableRefObject<
        Record<string, { min: number; max: number; recommended: number }>
    >;
}

export const Row = ({
    variable,
    modalResetKey,
    onOutOfBoundsChannel,
    onSubmit,
    originalLimitsRef,
}: Props) => {
    const {
        data: { optimized },
        variableValues,
    } = useBudgetOptimizationContext();
    const { variableNameMap } = useDashboardGlobalContext();

    const opt = optimized?.optimalVariables[variable];

    const originalLimits = originalLimitsRef.current[variable];
    const minRecommended =
        originalLimits?.min ??
        (optimized?.mediaLimits.find((limit) => limit.medium === variable)
            ?.lowerLimit ||
            0);
    const maxRecommended =
        originalLimits?.max ??
        (optimized?.mediaLimits.find((limit) => limit.medium === variable)
            ?.upperLimit ||
            0);
    const recommendedBudget =
        originalLimits?.recommended ?? (opt?.optimalSpend || 0);
    const sliderMin = 0;
    const sliderMax = Math.round((maxRecommended || 0) * 1.3);

    const formValues = variableValues[variable] || {
        locked: false,
        investment: 0,
    };

    const sliderConfig = {
        min: sliderMin,
        minRecommended,
        maxRecommended,
        recommendedBudget,
        max: sliderMax,
        investmentStep: optimized?.investmentStep,
    };

    const onOutOfBounds = (investment: number | null) => {
        const validation = validateBudgetRange(
            investment,
            minRecommended,
            maxRecommended,
        );

        if (!validation.isValid) {
            onOutOfBoundsChannel?.({
                channelName: variableNameMap[variable] || variable,
                min: minRecommended,
                max: maxRecommended,
            });
        }
    };

    const handleSubmit = (key, data) => {
        if (!data.locked) {
            onSubmit(key, data);
            return;
        }

        const validation = validateBudgetRange(
            data.investment,
            minRecommended,
            maxRecommended,
        );

        if (!validation.isValid) {
            onOutOfBoundsChannel?.({
                channelName: variableNameMap[variable] || variable,
                min: minRecommended,
                max: maxRecommended,
            });

            onSubmit(key, {
                ...data,
                investment: recommendedBudget,
            });
            return;
        }

        onSubmit(key, data);
    };

    return (
        <ModifyBudgetRow
            title={variableNameMap[variable] ?? variable}
            name={variable}
            sliderConfig={sliderConfig}
            formValues={formValues}
            onOutOfBounds={onOutOfBounds}
            modalResetKey={modalResetKey}
            onSubmit={handleSubmit}
            editPermissionFeatureFlag={
                FEATURE_FLAG_SUBJECTS.BUDGET_OPTIMIZATION_DASHBOARD_MODIFY_BUDGET_EDIT
            }
        />
    );
};
